import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckPenIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { castArray, isArray, noop, property } from 'lodash-es';
import RCSelect from 'rc-select';
import { Children, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from '../Checkbox';
import { Ellipsis } from '../Ellipsis';
import { renderRequiredLabel } from '../Input/Label';
import { Stack, StackItem } from '../Layout';
import { Text } from '../Typography';
import { createCustomSelect } from './BaseSelect';
import { SelectHandle } from './SelectHandle';
import { StyledTag } from './styled';
import { tokens } from './tokens';
import { CreatableOptionPosition, SelectSize } from './types';
import { useGetSelectedOptions } from './useSelectedOptions';
const CustomSelect = createCustomSelect(RCSelect);
const propertiesToCheck = ['value', 'label', 'children'];
const isExistingValue = ({ value, options, children }) => {
    if (isArray(options)) {
        // reuse existing function to find if value exists in options
        return findGroupNameInOptions(value, options).isFound;
    }
    if (isArray(children)) {
        function findIfValueExistsInChildren(children) {
            const isFound = children.some((child) => {
                // check if child is a ReactElement
                if (!isValidElement(child)) {
                    return false;
                }
                if (child.props.value === value) {
                    return true;
                }
                else if (child.props.children) {
                    return findIfValueExistsInChildren(Children.toArray(child.props.children));
                }
                return false;
            });
            return isFound;
        }
        return findIfValueExistsInChildren(Children.toArray(children));
    }
};
const findGroupNameInOptions = (value, options, groupName = '', isFound = false, isOptGroup = false) => {
    //Option can be a string as well for Select.Option in case of optGroup
    if (isArray(options)) {
        options?.some((option) => {
            if (!isFound) {
                const currentOption = isOptGroup ? option.props : option;
                const childOptions = currentOption.children;
                if (currentOption.value === value) {
                    isFound = true;
                }
                else if (typeof childOptions === 'object' || currentOption.options?.length) {
                    const childGroup = groupName ? groupName + '.' + currentOption.value : currentOption.value;
                    const childOptions = findGroupNameInOptions(value, currentOption.children || currentOption.options, childGroup, isFound, isOptGroup);
                    isFound = childOptions.isFound;
                    if (isFound) {
                        groupName = childOptions.groupName;
                    }
                }
            }
            return isFound;
        });
    }
    return { isFound, groupName };
};
export const renderSelectInput = ({ groupName, value, options, hasOptGroup, variant = 'primary', className, placeholder }) => {
    const group = groupName
        ? groupName
        : typeof options === 'object'
            ? findGroupNameInOptions(value, options, groupName, false, hasOptGroup).groupName
            : '';
    const placeholderText = placeholder ? (_jsx(Text, { level: 'sm', tone: theme.colors.gray400, children: placeholder })) : null;
    return (_jsx(SelectHandle, { size: 'm', variant: variant, className: className, children: _jsx(Ellipsis, { tooltip: true, children: value ? `${value}${group ? ` (${group})` : ''}` : placeholderText }) }));
};
export const defaultFilterOption = (inputValue, option) => {
    if (!option)
        return false;
    if (!inputValue)
        return true;
    return propertiesToCheck.some((property) => {
        const value = option[property];
        if (typeof value === 'string' || typeof value === 'number') {
            return value.toString().toLowerCase().includes(inputValue.toLowerCase());
        }
        return false;
    });
};
export function useValidateDropdownWidth(dropdownMatchSelectWidth, virtual) {
    useEffect(() => {
        if (virtual && !dropdownMatchSelectWidth) {
            console.error(`Virtualization is enabled, but dropdownMatchSelectWidth is not set to true. 
        In virtualization as only few options are rendered at a time, this will cause width of dropdown getting change while scroll as different items might have different width.
        `);
        }
    }, [virtual, dropdownMatchSelectWidth]);
}
export const defaultOptionRender = (option, dropdownMatchSelectWidth, isSelected, hideSelectedIcon, showCheckbox) => {
    return (_jsxs(Stack, { direction: 'horizontal', align: 'space-between', alignY: 'center', gap: theme.spaces.x8, children: [showCheckbox && (_jsx(StackItem, { shrink: '0', children: _jsx(Checkbox, { checked: Boolean(isSelected), "aria-label": `Select ${option.label}` }) })), _jsx(StackItem, { grow: '1', children: typeof option.label === 'string' && dropdownMatchSelectWidth ? (_jsx(Ellipsis, { tooltip: true, children: option.label })) : (option.label) }), !hideSelectedIcon && isSelected && !showCheckbox && (_jsx(StackItem, { shrink: '0', children: _jsx(CheckPenIcon, { type: 'extended', color: theme.colors.success600 }) }))] }));
};
export function Select({ menuItemSelectedIcon, filterOption = defaultFilterOption, value, onChange, onInputKeyDown, tagRender, renderInput, listItemHeight, dropdownMatchSelectWidth = true, virtual = true, mode, allowUnknown = true, showSearch = true, optionRender, creatable, renderCreatableLabel, creatableOptionPosition = CreatableOptionPosition.bottom, onCreateOption, onSearch, children: _children, options: _options, hideSelectedIcon = false, renderInputWithoutClass, label, required, defaultValue, showCheckbox = false, ...restProps }) {
    const [searchValue, setSearchValue] = useState('');
    const selectedValueMap = useMemo(() => {
        return Object.fromEntries(castArray(value).map((value) => [value, true]));
    }, [value]);
    const hasEmptyValueInOptionsLogged = useRef(false);
    const { options, children, hasEmptyValueInOptions } = useMemo(() => {
        let options = _options, children = _children;
        const isSearchValueExistingOption = isExistingValue({ value: searchValue, options, children });
        if (creatable && searchValue !== '' && !isSearchValueExistingOption) {
            const searchOptionToAdd = {
                label: renderCreatableLabel ? renderCreatableLabel(searchValue) : searchValue,
                value: searchValue,
                isNewOption: true
            };
            if (children && Children.toArray(children).length > 0) {
                const creatableOption = (_jsx(Select.Option, { ...searchOptionToAdd, children: searchOptionToAdd.label }, searchOptionToAdd.value));
                children =
                    creatableOptionPosition === CreatableOptionPosition.top
                        ? [creatableOption, ...Children.toArray(children)]
                        : [...Children.toArray(children), creatableOption];
            }
            else {
                // show search option in dropdown even if options is undefined
                options = options || [];
                options =
                    creatableOptionPosition === CreatableOptionPosition.top
                        ? [searchOptionToAdd, ...options]
                        : [...options, searchOptionToAdd];
            }
        }
        const hasEmptyValueInOptions = isExistingValue({ value: '', options, children });
        if (hasEmptyValueInOptions && !hasEmptyValueInOptionsLogged.current) {
            // log only once per component instance
            console.warn('Empty value in options is not allowed', { options, children });
            hasEmptyValueInOptionsLogged.current = true;
            // its okay that there can be a scenario where options can be reset and the flag is not reset and the error is not logged properly
        }
        return { options, children, hasEmptyValueInOptions };
    }, [creatable, _options, _children, searchValue, renderCreatableLabel, creatableOptionPosition]);
    const getSelectedOptions = useGetSelectedOptions({ options, children });
    useValidateDropdownWidth(dropdownMatchSelectWidth, virtual);
    const listItemCurrentHeight = listItemHeight || tokens.Menu.size[restProps.size || SelectSize.m].listItemHeight;
    const persistentOnChange = usePersistentCallback(onChange || noop);
    // `allowUnknown: false` will reset the value if selected value doesnt exist in options,
    // useful when select options change dynamically
    useEffect(() => {
        if (allowUnknown)
            return;
        const selectedOptions = getSelectedOptions(value);
        const options = castArray(selectedOptions);
        const validOptions = options.filter(Boolean);
        if (validOptions.length !== options.length) {
            if (Array.isArray(selectedOptions)) {
                persistentOnChange(validOptions.map(property('value')), validOptions);
            }
            else if (value) {
                // RC select also sends undefined as option when clearing
                persistentOnChange(undefined, selectedOptions);
            }
        }
    }, [value, allowUnknown, getSelectedOptions, persistentOnChange]);
    //Putting a logic for duplicate tag added to show the warning if user is typing the value and pressing enter to add a new tag which already exists in values
    let isDuplicateKeyAdded = useRef(false);
    const handleInputKeyDown = (e) => {
        onInputKeyDown?.(e);
        const currentValue = e?.currentTarget?.value;
        if ((e.key === 'Enter' || (e.code === 'Enter' && typeof value === 'object')) && isArray(value)) {
            isDuplicateKeyAdded.current = value.some((v) => v === currentValue);
        }
    };
    const handleChange = (v, option) => {
        if (!isDuplicateKeyAdded.current) {
            onChange?.(v, option);
            if (creatable) {
                const selectedOptions = castArray(option);
                const optionAddedUsingSearch = selectedOptions.find((currentOption) => currentOption.isNewOption);
                if (optionAddedUsingSearch) {
                    const newlyAddedOption = { label: optionAddedUsingSearch.label, value: optionAddedUsingSearch.value };
                    onCreateOption?.(newlyAddedOption);
                }
                // reset search value after an option is selected
                setSearchValue('');
            }
        }
        else {
            // set value false if user stop typing and choose other option from list then onChange should work
            isDuplicateKeyAdded.current = false;
        }
    };
    const handleSearch = (value) => {
        onSearch?.(value);
        if (creatable) {
            setSearchValue(value);
        }
    };
    const selectProps = {
        tagRender: tagRender
            ? tagRender
            : ({ label, value, onClose, closable, disabled }) => {
                return (_jsx(StyledTag, { icon: null, disabled: disabled, closable: closable, onClose: onClose, children: label }, value));
            },
        onInputKeyDown: handleInputKeyDown,
        // null or empty string for value results in hiding the placeholder
        value: value === null || (value === '' && !hasEmptyValueInOptions) ? undefined : value,
        virtual,
        dropdownMatchSelectWidth,
        ...restProps,
        label: renderRequiredLabel({ label, required }),
        listItemHeight: listItemCurrentHeight,
        onChange: handleChange,
        optionRender: optionRender
            ? (option, info) => optionRender(option, info, dropdownMatchSelectWidth, !!option.value && selectedValueMap[option.value])
            : (option) => defaultOptionRender(option, dropdownMatchSelectWidth, !!option.value && selectedValueMap[option.value], hideSelectedIcon, showCheckbox),
        menuItemSelectedIcon: menuItemSelectedIcon ?? null,
        filterOption,
        // null or empty string for defaultValue results in hiding the placeholder
        defaultValue: defaultValue === null || defaultValue === '' ? undefined : defaultValue,
        showSearch,
        mode: mode,
        onSearch: handleSearch,
        options,
        children
    };
    if (renderInput) {
        const _children = (children || []);
        const _options = _children.length ? _children : options;
        // eslint-disable-next-line react-compiler/react-compiler
        selectProps.getRawInputElement = () => (_jsx(Stack, { className: renderInputWithoutClass ? '' : restProps.className, children: renderInput({
                value: value || defaultValue,
                variant: restProps.variant,
                options: _options,
                groupName: '',
                hasOptGroup: !!_children.length,
                placeholder: restProps.placeholder
            }) }));
    }
    return _jsx(CustomSelect, { ...selectProps });
}
Select.Option = RCSelect.Option;
Select.OptGroup = RCSelect.OptGroup;
